import { useState, useEffect, lazy, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2, Download, FileText, AlertCircle, ArrowLeft, File } from "lucide-react";

const PdfViewer = lazy(() => import("@/components/ui/PdfViewer"));
import {
  downloadAndCreateBlobUrl,
  isValidBucket,
  isPdfFile,
  isImageFile,
  type SupportedBucket,
} from "@/lib/documentPreview";
import { useDocumentAccessLog } from "@/hooks/useDocumentAccessLog";

/**
 * Standalone Document Preview Page
 * 
 * Renders documents in a dedicated route to avoid "white page" issues
 * when opening documents in new tabs on iOS/Mobile.
 * 
 * Route: /preview?bucket=...&path=...&name=...&type=...
 */
const DocumentPreviewPage = () => {
  const [searchParams] = useSearchParams();
  
  const bucket = searchParams.get("bucket") || "";
  const filePath = searchParams.get("path") || "";
  const fileName = searchParams.get("name") || "Dokument";
  const mimeType = searchParams.get("type") || "";
  const documentId = searchParams.get("docId") || "";
  const documentType = (searchParams.get("docType") as "employee" | "applicant") || "employee";
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  
  const { logPreviewOpened, logPreviewFailed, logDownloadRequested } = useDocumentAccessLog();

  useEffect(() => {
    if (bucket && filePath) {
      loadDocument();
    } else {
      setError("Fehlende Dokumentinformationen");
      setLoading(false);
    }

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [bucket, filePath]);

  const loadDocument = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!isValidBucket(bucket)) {
        throw new Error("Ungültiger Storage-Bucket");
      }

      const url = await downloadAndCreateBlobUrl(bucket as SupportedBucket, filePath, mimeType || undefined);
      setBlobUrl(url);
      
      // DSGVO Audit-Log: Preview erfolgreich geöffnet
      if (documentId) {
        logPreviewOpened(documentId, documentType, "preview_route");
      }
    } catch (err: any) {
      console.error("Error loading document:", err);
      setError(err.message || "Fehler beim Laden des Dokuments");
      
      // DSGVO Audit-Log: Preview fehlgeschlagen
      if (documentId) {
        logPreviewFailed(documentId, documentType, "preview_route");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!blobUrl) return;

    // DSGVO Audit-Log: Download angefordert
    if (documentId) {
      logDownloadRequested(documentId, documentType, "download_button");
    }

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleGoBack = () => {
    window.close();
    setTimeout(() => {
      window.history.back();
    }, 100);
  };

  const isPdf = isPdfFile(mimeType, fileName);
  const isImage = isImageFile(mimeType, fileName);
  const canPreview = isPdf || isImage;

  useEffect(() => {
    document.title = `${fileName} - Dokumentvorschau`;
  }, [fileName]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b bg-card px-4 py-3 flex items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={handleGoBack} title="Zurück">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
          <span className="font-medium truncate">{fileName}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownload}
          disabled={!blobUrl}
        >
          <Download className="h-4 w-4 mr-2" />
          <span className="hidden sm:inline">Herunterladen</span>
        </Button>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center p-4 overflow-auto">
        {loading ? (
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Dokument wird geladen...</p>
          </div>
        ) : error ? (
          <div className="text-center max-w-md px-4">
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Fehler beim Laden</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="outline" onClick={handleGoBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Zurück
              </Button>
              <Button onClick={loadDocument}>
                Erneut versuchen
              </Button>
            </div>
          </div>
        ) : blobUrl ? (
          <>
            {isPdf && (
              <Suspense fallback={
                <div className="flex items-center justify-center h-[80vh]">
                  <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
              }>
                <PdfViewer url={blobUrl} className="w-full min-h-[80vh]" />
              </Suspense>
            )}
            {isImage && (
              <img
                src={blobUrl}
                alt={fileName}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-lg"
              />
            )}
            {!canPreview && (
              <div className="text-center max-w-md px-4">
                <File className="h-20 w-20 text-muted-foreground mx-auto mb-6" />
                <h2 className="text-xl font-semibold mb-2">Vorschau nicht verfügbar</h2>
                <p className="text-muted-foreground mb-6">
                  Dieser Dateityp kann nicht im Browser angezeigt werden.
                </p>
                <Button onClick={handleDownload}>
                  <Download className="h-4 w-4 mr-2" />
                  Datei herunterladen
                </Button>
              </div>
            )}
          </>
        ) : null}
      </main>
    </div>
  );
};

export default DocumentPreviewPage;
